import { useNavigate } from 'react-router';
import { ArrowForward, Create } from '@material-ui/icons';
import { ResponseMessage } from '../../../components/ResponseMessage';
import { LabeledText } from '../../../components/LabeledText';
import { Progress } from '../../../components/ProgressBar/Progress';
import { Button } from '../../../components/Button/NewButton';

interface Props {
  account: CreateAccount;
  accountExists: boolean;
  onBack(): void;
  errors: CreateAccountError[];
  progress: Progress;
}

export const CreateAccountLoading = ({
  account,
  accountExists,
  errors,
  onBack,
  progress,
}: Props) => {
  const navigate = useNavigate();

  const getResponseType = (): 'error' | 'warning' | 'success' => {
    if (accountExists && !errors.length) return 'success';
    if (accountExists && errors.length) return 'warning';
    else return 'error';
  };

  const getResponseMessage = (): string => {
    if (accountExists && !errors.length)
      return `${account.accountNumber} Created Successfully!`;
    if (accountExists && errors.length)
      return `${account.accountNumber} Created with Errors`;
    else return `${account.accountNumber} Not Created`;
  };

  const getResponseSubMessage = (): string | undefined => {
    if (accountExists && !errors.length && account.devices.length)
      return 'All devices added successfully';
    if (accountExists && errors.length < account.devices.length)
      return 'Some devices did not add successfully. See error details below';
    if (accountExists && !errors.length && !account.devices.length)
      return undefined;
    return 'See error details below';
  };

  return (
    <div className="flex flex-col gap-4 w-[400px]">
      {progress.current >= progress.total ? (
        <ResponseMessage
          responseType={getResponseType()}
          message={getResponseMessage()}
          subMessage={getResponseSubMessage()}
        />
      ) : (
        <h1 className="text-2xl mb-4">Creating Account...</h1>
      )}
      {progress.current < progress.total ? (
        <>
          <Progress
            className="h-4"
            indeterminate={progress.current === 0}
            value={
              progress.current !== 0
                ? Math.floor((progress.current / progress.total) * 100)
                : undefined
            }
          />
          <p className="text-xs text-right w-full">{progress.message}</p>
        </>
      ) : null}
      {progress.current === progress.total && errors.length ? (
        <div className="flex flex-col justify-start items-center gap-4 w-full max-h-[200px] overflow-auto p-4">
          <p className="text-left text-primary w-full">Error Details:</p>
          {errors.map((error, index) => (
            <div
              className="flex flex-row flex-wrap justify-start w-full gap-2"
              key={index}
            >
              <p>{index + 1} - </p>
              {error.type === 'account' ? (
                <>
                  <LabeledText
                    align="left"
                    label="Error Code"
                    text={error.name}
                    labelSize={12}
                    textSize={14}
                    orientation="horizontal"
                  />
                  <LabeledText
                    align="left"
                    label="Error"
                    text={error.originalError ?? undefined}
                    labelSize={12}
                    textSize={14}
                    orientation="horizontal"
                  />
                </>
              ) : (
                <>
                  <LabeledText
                    align="left"
                    label="Location"
                    text={error.name}
                    labelSize={12}
                    textSize={14}
                    orientation="horizontal"
                  />
                  <LabeledText
                    align="left"
                    label="Device MAC"
                    text={error.description}
                    labelSize={12}
                    textSize={14}
                    orientation="horizontal"
                  />
                  <LabeledText
                    align="left"
                    label="Error"
                    text={error.originalError ?? ''}
                    labelSize={12}
                    textSize={14}
                    orientation="horizontal"
                  />
                </>
              )}
            </div>
          ))}
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <Button
          className="md:col-span-6"
          startIcon={<Create />}
          fullWidth
          onClick={() => onBack()}
        >
          Create Another
        </Button>
        {accountExists ||
        errors.find((error) =>
          (error.originalError ?? '').toLowerCase().includes('exists')
        ) ? (
          <Button
            className="md:col-span-6"
            endIcon={<ArrowForward />}
            fullWidth
            onClick={() => navigate(`/manage/account/${account.accountNumber}`)}
          >
            Go to account
          </Button>
        ) : null}
      </div>
    </div>
  );
};
